import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import type { Empresa } from '../../types';

interface AuthorizedUser {
  id: string;
  email: string;
  username?: string;
  isAdmin: boolean;
  empresa?: string;
  sector?: string;
  addedBy?: string;
  addedAt?: Timestamp;
}

interface UsersSectionProps {
  userEmail?: string;
  empresas: Empresa[];
}

export const UsersSection: React.FC<UsersSectionProps> = ({ userEmail, empresas }) => {
  const [users, setUsers] = useState<AuthorizedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<AuthorizedUser>>({});

  const sectorOptions = ['solidos', 'fluidos', 'supervisor'];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersSnapshot = await getDocs(collection(db, 'usuariosAutorizados'));
      const usersData: AuthorizedUser[] = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        email: doc.id, // Document ID is the email
        username: doc.data().username,
        isAdmin: doc.data().isAdmin || false,
        empresa: doc.data().empresa,
        sector: doc.data().sector,
        addedBy: doc.data().addedBy,
        addedAt: doc.data().addedAt
      }));
      setUsers(usersData.sort((a, b) => a.email.localeCompare(b.email)));
      setError(null);
    } catch (err: any) {
      setError('Error al cargar usuarios: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: AuthorizedUser) => {
    setEditingUser(user.id);
    setEditData({
      username: user.username,
      isAdmin: user.isAdmin,
      empresa: user.empresa,
      sector: user.sector
    });
  };

  const handleSave = async (userId: string) => {
    try {
      const userRef = doc(db, 'usuariosAutorizados', userId);
      await updateDoc(userRef, {
        username: editData.username || '',
        isAdmin: editData.isAdmin || false,
        empresa: editData.empresa || '',
        sector: editData.sector || '',
        updatedBy: userEmail,
        updatedAt: Timestamp.now()
      });
      setEditingUser(null);
      setEditData({});
      await loadUsers();
      alert('Usuario actualizado correctamente');
    } catch (err: any) {
      alert('Error al actualizar usuario: ' + err.message);
    }
  };

  const handleDelete = async (userId: string, email: string) => {
    if (!window.confirm(`¿Estás seguro de eliminar el usuario ${email}?\n\nEsto no elimina la cuenta de Firebase Authentication, solo revoca el acceso a la app.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'usuariosAutorizados', userId));
      await loadUsers();
      alert('Usuario eliminado correctamente');
    } catch (err: any) {
      alert('Error al eliminar usuario: ' + err.message);
    }
  };

  const toggleAdmin = async (userId: string, currentValue: boolean) => {
    try {
      const userRef = doc(db, 'usuariosAutorizados', userId);
      await updateDoc(userRef, {
        isAdmin: !currentValue,
        updatedBy: userEmail,
        updatedAt: Timestamp.now()
      });
      await loadUsers();
    } catch (err: any) {
      alert('Error al cambiar permisos: ' + err.message);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Cargando usuarios...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: 'red', textAlign: 'center' }}>
        {error}
        <br />
        <button onClick={loadUsers} style={{ marginTop: 10 }}>Reintentar</button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <h2>👥 Gestión de Usuarios Autorizados</h2>
      <p style={{ color: '#666', marginBottom: 20 }}>
        Total de usuarios: {users.length} | Administradores: {users.filter(u => u.isAdmin).length}
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Email</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nombre</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Empresa</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Sector</th>
              <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Admin</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Agregado Por</th>
              <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: 12 }}>
                  {user.email}
                  {user.email === userEmail && (
                    <span style={{
                      marginLeft: 8,
                      fontSize: 11,
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: 3
                    }}>
                      TÚ
                    </span>
                  )}
                </td>
                <td style={{ padding: 12 }}>
                  {editingUser === user.id ? (
                    <input
                      type="text"
                      value={editData.username || ''}
                      onChange={(e) => setEditData({ ...editData, username: e.target.value })}
                      style={{
                        width: '100%',
                        padding: 6,
                        border: '1px solid #ddd',
                        borderRadius: 4
                      }}
                      placeholder="Nombre del usuario"
                    />
                  ) : (
                    user.username || <span style={{ color: '#999', fontStyle: 'italic' }}>Sin nombre</span>
                  )}
                </td>
                <td style={{ padding: 12 }}>
                  {editingUser === user.id ? (
                    <select
                      value={editData.empresa || ''}
                      onChange={(e) => setEditData({ ...editData, empresa: e.target.value })}
                      style={{
                        width: '100%',
                        padding: 6,
                        border: '1px solid #ddd',
                        borderRadius: 4
                      }}
                    >
                      <option value="">-- Seleccionar --</option>
                      {empresas.map(emp => (
                        <option key={emp.id} value={emp.codigo}>{emp.nombre}</option>
                      ))}
                    </select>
                  ) : (
                    user.empresa || <span style={{ color: '#999', fontStyle: 'italic' }}>Sin asignar</span>
                  )}
                </td>
                <td style={{ padding: 12 }}>
                  {editingUser === user.id ? (
                    <select
                      value={editData.sector || ''}
                      onChange={(e) => setEditData({ ...editData, sector: e.target.value })}
                      style={{
                        width: '100%',
                        padding: 6,
                        border: '1px solid #ddd',
                        borderRadius: 4
                      }}
                    >
                      <option value="">-- Seleccionar --</option>
                      {sectorOptions.map(sector => (
                        <option key={sector} value={sector}>{sector.charAt(0).toUpperCase() + sector.slice(1)}</option>
                      ))}
                    </select>
                  ) : (
                    user.sector ? (
                      <span>{user.sector.charAt(0).toUpperCase() + user.sector.slice(1)}</span>
                    ) : (
                      <span style={{ color: '#999', fontStyle: 'italic' }}>Sin asignar</span>
                    )
                  )}
                </td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  {editingUser === user.id ? (
                    <input
                      type="checkbox"
                      checked={editData.isAdmin || false}
                      onChange={(e) => setEditData({ ...editData, isAdmin: e.target.checked })}
                      style={{ transform: 'scale(1.3)' }}
                    />
                  ) : (
                    <button
                      onClick={() => toggleAdmin(user.id, user.isAdmin)}
                      disabled={user.email === userEmail}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: user.isAdmin ? '#FF9800' : '#ddd',
                        color: user.isAdmin ? 'white' : '#333',
                        border: 'none',
                        borderRadius: 4,
                        cursor: user.email === userEmail ? 'not-allowed' : 'pointer',
                        fontSize: 12,
                        fontWeight: 'bold'
                      }}
                      title={user.email === userEmail ? 'No puedes cambiar tus propios permisos' : ''}
                    >
                      {user.isAdmin ? '✓ ADMIN' : '- USER'}
                    </button>
                  )}
                </td>
                <td style={{ padding: 12, fontSize: 13, color: '#666' }}>
                  {user.addedBy || <span style={{ fontStyle: 'italic' }}>Desconocido</span>}
                </td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  {editingUser === user.id ? (
                    <>
                      <button
                        onClick={() => handleSave(user.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          marginRight: 8,
                          fontSize: 12
                        }}
                      >
                        ✓ Guardar
                      </button>
                      <button
                        onClick={() => {
                          setEditingUser(null);
                          setEditData({});
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#999',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        ✗ Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEdit(user)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#2196F3',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          marginRight: 8,
                          fontSize: 12
                        }}
                      >
                        ✎ Editar
                      </button>
                      <button
                        onClick={() => handleDelete(user.id, user.email)}
                        disabled={user.email === userEmail}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: user.email === userEmail ? '#ccc' : '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: user.email === userEmail ? 'not-allowed' : 'pointer',
                          fontSize: 12
                        }}
                        title={user.email === userEmail ? 'No puedes eliminarte a ti mismo' : ''}
                      >
                        🗑 Eliminar
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{
        marginTop: 30,
        padding: 20,
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: 8
      }}>
        <h3 style={{ marginTop: 0 }}>⚠️ Importante</h3>
        <ul style={{ textAlign: 'left', marginBottom: 0 }}>
          <li>Eliminar un usuario aquí solo revoca su acceso a la app</li>
          <li>Para eliminar completamente la cuenta, debes hacerlo en Firebase Authentication</li>
          <li>No puedes eliminar tu propia cuenta ni cambiar tus propios permisos de admin</li>
          <li>Los usuarios necesitan estar en esta lista para poder acceder a la app</li>
          <li><strong>Empresa y Sector:</strong> Asigna la empresa y sector de cada usuario para controlar el acceso a funciones específicas en la app móvil</li>
          <li><strong>Sectores disponibles:</strong> Sólidos, Fluidos, Supervisor (cada uno con diferentes permisos de acceso)</li>
        </ul>
      </div>
    </div>
  );
};
